import * as React from 'react';
import {
  ACTION_CLICK_BUSINESS_CARD,
  ACTION_CLICK_BUSINESS_GROUP_CARD,
  ACTION_CLICK_METRO_AREA_CARD,
  ACTION_SELECT_EXPERIENCE_CARD,
  EventCustomAttributes,
} from 'utils/analytics/eventTypeConstantsAmplitude';
import { BusinessCard } from 'consumer-page-widgets/BusinessCard';
import { ExperienceCard } from 'consumer-page-widgets/ExperienceCard';
import { WidgetListItemContent } from 'types';
import { businessToData, experienceOfferingToData } from 'utils/analytics/amplitude';
import { centerCropThumbnailUrl } from '@tock/images';
import { consumer } from '@tock/coretypes';
import { getBodyContent, getTockContent, getTockMedium } from 'utils/widgetUtils';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';

interface Props {
  modifiers?: consumer.WidgetModifier[];
  item: WidgetListItemContent;
  cardClassName?: string;
  pathname: string;
  index?: number;
  widgetListIndex?: number;
  widgetName?: string;
  className?: string;
}

export const WidgetCard: React.FunctionComponent<Props> = ({
  item,
  modifiers,
  className,
  pathname,
  index,
  widgetListIndex,
  widgetName,
}) => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  switch (item.type) {
    case 'EXPERIENCE': {
      const {
        date,
        altText,
        title,
        subtitle,
        relativeLink,
        dominantColor,
        imgSrc,
        price,
        business,
        location: experienceLocation,
        experience,
        schedule,
      } = item;

      const onClick = () => {
        amplitudeSendEvent(ACTION_SELECT_EXPERIENCE_CARD, {
          listIndex: index,
          widgetName,
          pageWidgetListIndex: widgetListIndex,
          ...businessToData(business),
          ...experienceOfferingToData(experience),
        });
      };

      return (
        <ExperienceCard
          className={className}
          altText={altText}
          business={business}
          dominantColor={dominantColor}
          href={`${relativeLink}?tock_source=tock&tock_medium=${getTockMedium(
            pathname
          )}&tock_content=${getTockContent('carousel', title)}`}
          image={centerCropThumbnailUrl(imgSrc || '')}
          location={experienceLocation}
          priceCents={price}
          subtitle={subtitle}
          schedule={schedule}
          title={title}
          date={date}
          widgetModifiers={modifiers}
          index={index}
          onClick={onClick}
        />
      );
    }
    case 'BUSINESS':
    case 'BUSINESS_GROUP':
    case 'METRO_AREA': {
      {
        const {
          body,
          highlightedNote,
          altText,
          title,
          subtitle,
          subtitleDetailed,
          relativeLink,
          dominantColor,
          imgSrc,
        } = item;

        const onClick = () => {
          switch (item.type) {
            case 'BUSINESS': {
              const data: EventCustomAttributes = {
                listIndex: index,
                widgetName,
                pageWidgetListIndex: widgetListIndex,
                ...businessToData(item.business),
              };
              amplitudeSendEvent(ACTION_CLICK_BUSINESS_CARD, data);
              return;
            }
            case 'BUSINESS_GROUP': {
              const data: EventCustomAttributes = {
                listIndex: index,
                widgetName,
                pageWidgetListIndex: widgetListIndex,
                businessGroupName: title,
              };
              amplitudeSendEvent(ACTION_CLICK_BUSINESS_GROUP_CARD, data);
              return;
            }
            case 'METRO_AREA': {
              const data: EventCustomAttributes = {
                listIndex: index,
                widgetName,
                pageWidgetListIndex: widgetListIndex,
              };
              amplitudeSendEvent(ACTION_CLICK_METRO_AREA_CARD, data);
              return;
            }
            default:
              return;
          }
        };
        return (
          <BusinessCard
            className={className}
            altText={altText}
            title={title}
            subtitle={subtitle}
            subtitleDetailed={subtitleDetailed}
            href={`${relativeLink}?tock_source=tock&tock_medium=${getTockMedium(
              pathname
            )}&tock_content=${getTockContent('carousel', title)}`}
            dominantColor={dominantColor}
            image={centerCropThumbnailUrl(imgSrc || '')}
            body={getBodyContent(body)}
            showBody={false}
            highlightedNote={highlightedNote}
            widgetModifiers={modifiers}
            index={index}
            onClick={onClick}
          />
        );
      }
    }
  }

  return null;
};
